// components/EventLogPanel.jsx: Phase 3 #10 operations-log panel.
//
// Renders the accumulated, pre-formatted event-log lines (from the game
// reducer's eventLog, oldest → newest) beside the game canvas. The panel
// is a thin presentational shell, all the wording lives in the pure
// game/event-log.js formatter, which this component never calls.
//
// Layout: the panel takes SIDE space next to the fixed-size canvas (the
// parent is a flex row), so it never shrinks the table below its
// layout.js minimum. On narrow viewports the flex row wraps and the
// panel stacks below the table (see networked-game.css .event-log-panel).
//
// Newest line stays visible: the list auto-scrolls to the bottom on each
// update.

import { useEffect, useRef } from 'preact/hooks';

/**
 * @param {{ lines?: string[] }} props
 */
export function EventLogPanel({ lines = [] }) {
  /** @type {{ current: HTMLOListElement | null }} */
  const listRef = useRef(null);

  // Keep the newest line in view, scroll to the bottom whenever the
  // log grows. aria-live announces new lines to screen readers.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines.length]);

  return (
    <aside class="event-log-panel cream" data-testid="event-log-panel" aria-label="Game log">
      <h2 class="event-log-title">Game log</h2>
      <ol
        ref={listRef}
        class="event-log-list"
        data-testid="event-log-list"
        aria-live="polite"
        aria-atomic="false"
      >
        {lines.length === 0 ? (
          <li class="event-log-empty" data-testid="event-log-empty">
            No plays yet.
          </li>
        ) : (
          lines.map((line, i) => (
            // Lines are append-only + capped; index within the current
            // window is a stable enough key for this read-only list.
            <li key={`${i}-${line}`} class="event-log-line" data-testid="event-log-line">
              {line}
            </li>
          ))
        )}
      </ol>
    </aside>
  );
}
